import { useTranslation } from "react-i18next";
import { useAppearance, THEME_COLORS } from "../contexts/AppearanceContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Paintbrush, Check, Sun, Moon } from "lucide-react";

const COLOR_OPTIONS = [
  { key: "blue", label: "Azul" },
  { key: "emerald", label: "Verde" },
  { key: "violet", label: "Violeta" },
  { key: "rose", label: "Rosa" },
  { key: "orange", label: "Laranja" },
  { key: "cyan", label: "Cyan" },
  { key: "amber", label: "\u00c2mbar" },
  { key: "indigo", label: "Indigo" },
  { key: "pink", label: "Pink" },
  { key: "teal", label: "Teal" },
];

export default function AparenciaPage() {
  const { t } = useTranslation();
  const appearance = useAppearance();
  const { updateAppearance } = appearance;

  return (
    <div data-testid="aparencia-page" className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${appearance.colors?.["50"]}` }}>
          <Paintbrush className="w-5 h-5" style={{ color: appearance.colors?.["500"] }} strokeWidth={1.5} />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-900 tracking-tight">{t("configuracoes.aparencia.title")}</h1>
          <p className="text-slate-500 mt-0.5">{t("configuracoes.aparencia.subtitle")}</p>
        </div>
      </div>

      {/* Theme Color */}
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-heading text-slate-900 flex items-center gap-2"><Paintbrush className="w-5 h-5" style={{ color: appearance.colors?.["500"] }} />{t("ui.cor_do_tema")}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500 mb-4">Escolha a cor principal do sistema. Ela será aplicada em botões, menus ativos, avatares e ícones.</p>
          <div className="grid grid-cols-5 sm:grid-cols-10 gap-3">
            {COLOR_OPTIONS.map(c => (
              <button key={c.key} onClick={() => updateAppearance({ theme_color: c.key })}
                className={`flex flex-col items-center gap-1.5 p-2 rounded-lg border-2 transition-all ${appearance.theme_color === c.key ? "scale-105" : "border-transparent hover:border-slate-200"}`}
                style={appearance.theme_color === c.key ? { borderColor: THEME_COLORS[c.key]["500"] } : undefined}>
                <div className="w-10 h-10 rounded-lg" style={{ backgroundColor: THEME_COLORS[c.key]["500"] }}>
                  {appearance.theme_color === c.key && <div className="w-full h-full flex items-center justify-center"><Check className="w-5 h-5 text-white" /></div>}
                </div>
                <span className="text-[10px] text-slate-500">{c.label}</span>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Sidebar Mode */}
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-heading text-slate-900 flex items-center gap-2"><Sun className="w-5 h-5" style={{ color: appearance.colors?.["500"] }} />{t("ui.modo_sidebar")}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500 mb-4">{t("ui.escolha_sidebar")}</p>
          <div className="flex gap-4">
            <button onClick={() => updateAppearance({ sidebar_mode: "light" })}
              className={`flex-1 p-4 rounded-lg border-2 transition-all ${appearance.sidebar_mode === "light" ? "bg-transparent" : "border-slate-200 hover:border-slate-300"}`}
              style={appearance.sidebar_mode === "light" ? { borderColor: appearance.colors?.["500"] } : undefined}>
              <div className="w-full h-20 bg-gradient-to-b from-white to-[#eaeaea] rounded-md border border-slate-200 mb-3 flex items-center justify-center">
                <Sun className="w-6 h-6 text-slate-400" />
              </div>
              <p className="text-sm font-medium text-slate-800">{t("ui.sidebar_clara")}</p>
              <p className="text-xs text-slate-400">{t("ui.sidebar_clara_desc")}</p>
            </button>
            <button onClick={() => updateAppearance({ sidebar_mode: "dark" })}
              className={`flex-1 p-4 rounded-lg border-2 transition-all ${appearance.sidebar_mode === "dark" ? "bg-transparent" : "border-slate-200 hover:border-slate-300"}`}
              style={appearance.sidebar_mode === "dark" ? { borderColor: appearance.colors?.["500"] } : undefined}>
              <div className="w-full h-20 bg-slate-800 rounded-md border border-slate-700 mb-3 flex items-center justify-center">
                <Moon className="w-6 h-6 text-slate-400" />
              </div>
              <p className="text-sm font-medium text-slate-800">{t("ui.sidebar_escura")}</p>
              <p className="text-xs text-slate-400">{t("ui.sidebar_escura_desc")}</p>
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
